import { useMemo } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import WplButton from "@/components/wpl/button/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import { ProductKeyTypes } from "@/libraries/wpl/foundations/src";

import { GlobalNavNavigationBar } from "./nav-global";
import GlobalNavLinksContainer, {
    GlobalNavLinkContentProps,
    GlobalNavProvider
} from "./nav-links-container";

export type CompoundSubLinksAndCtaProps = {
    compoundSubNavLinksProps: GlobalNavLinkContentProps[];
    isShowCta?: boolean;
    ctaButtonText?: string;
    ctaUrl?: string;
    productKey?: ProductKeyTypes;
};

export type CompoundSubNavProps = {
    compoundSubNavTitle: string;
    compoundSubNavLinksProps: GlobalNavLinkContentProps[];
    isShowCta?: boolean;
    ctaButtonText?: string;
    ctaUrl?: string;
    productKey?: ProductKeyTypes;
};

export type GlobalCompondNavProps = {
    productKey: ProductKeyTypes;
    isShowSearchBarAndProfileContainer?: boolean;
    isShowTryNowLink?: boolean;
    isShowSearchButton?: boolean;
    globalLinksProps: GlobalNavLinkContentProps[];
    compoundSubNavLinksProps: GlobalNavLinkContentProps[];
    compoundSubNavTitle: string;
    isShowCta?: boolean;
    ctaButtonText?: string;
    ctaUrl?: string;
};

const compoundSubNavCSS = css({
    "borderBlockStart": "0.75px solid #DDDEE1",
    "width": "100%",
    "display": "flex",
    "justifyContent": "center",

    ".compoundSubNav-bar-content": {
        "alignItems": "baseline",
        "display": "none",
        "justifyContent": "space-between",
        "flex": 1,
        "paddingInline": "40px",
        "paddingBlock": token("space.200"),
        "flexDirection": "row",
        "position": "relative",
        "zIndex": 1,
        "marginInline": "60px",
        "width": "100%",
        [media.above.sm]: {
            display: "flex",
            maxWidth: "1152px",
            paddingInline: "0"
        },

        ".compoundSubNav-bar-title": {
            textDecoration: "none",
            fontWeight: "500",
            fontSize: "1.45rem"
        },

        ".compoundSubNavLinksAndCta-container": {
            "display": "flex",
            "alignItems": "flex-end",
            "width": "100%",
            "justifyContent": "space-between",

            ".navigation-links": {
                "display": "grid",
                "gridTemplateColumns": "repeat(5, max-content)",
                "gap": token("space.400"),
                "alignItems": "end",
                "listStyle": "none",
                "margin": 0,
                "padding": 0,
                "marginInlineStart": token("space.250"),
                ".chevron-icon": {
                    display: "block",
                    color: "#0052cc",
                    marginInlineStart: "8px",
                    lineHeight: 0
                },
                "> li.nav-link": {
                    "> a.nav-link-anchor": {
                        "display": "flex",
                        "alignItems": "center",
                        "color": "#42526e",
                        "textDecoration": "none",
                        "lineHeight": "1rem",
                        "marginBlockEnd": "-1px",
                        "&.isActive": {
                            color: "#0052cc"
                        }
                    },
                    [media.above.md]: {
                        "> a.nav-link-anchor": {
                            borderRadius: "3px",
                            textDecoration: "none"
                        }
                    }
                },
                [media.above.md]: {
                    marginInlineStart: token("space.400")
                }
            },

            ".compoundSubNav-trynow a": {
                borderRadius: "25px 25px 25px 25px",
                alignItems: "center"
            }
        }
    }
});

const globalCompoundNavCss = css({
    ".mobile-nav": {
        display: "none"
    },

    [media.below.sm]: {
        ".desktop-nav": {
            display: "none"
        },

        ".mobile-nav": {
            display: "block"
        }
    },

    ".desktop-nav": {
        width: "100%"
    },

    ".global-compond-nav": {
        "alignItems": "center",
        "background": "#ffffff",
        "display": "flex",
        "flexDirection": "column",
        "justifyContent": "center",
        "position": "relative",
        "zIndex": 5,
        ".navigation-bar": {
            width: "100%"
        }
    }
});

const CompoundSubLinksAndCta = ({
    compoundSubNavLinksProps,
    isShowCta,
    ctaButtonText = "Contact Sales",
    ctaUrl = "#",
    productKey = "jiraSoftware"
}: CompoundSubLinksAndCtaProps) => {
    const ctaButtonUrl = useLocalizedHref(ctaUrl);
    return (
        <div className="compoundSubNavLinksAndCta-container">
            <GlobalNavLinksContainer
                globalLinksProps={compoundSubNavLinksProps}
            />
            {isShowCta && (
                <div className="compoundSubNav-trynow">
                    <WplButton
                        text={ctaButtonText}
                        url={ctaButtonUrl}
                        size="small"
                        dataEventProps={{
                            "data-product-key": productKey,
                            "data-event": "clicked",
                            "data-event-component": "linkButton",
                            "data-event-container": "navbar"
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export const CompoundSubNav = ({
    compoundSubNavTitle,
    compoundSubNavLinksProps,
    isShowCta,
    ctaButtonText,
    ctaUrl,
    productKey = "jiraSoftware"
}: CompoundSubNavProps) => {
    return (
        <div className="compoundSubNav-bar" css={compoundSubNavCSS}>
            <div className="compoundSubNav-bar-content">
                <div className="compoundSubNav-bar-title">
                    {compoundSubNavTitle}
                </div>
                <CompoundSubLinksAndCta
                    compoundSubNavLinksProps={compoundSubNavLinksProps}
                    isShowCta={isShowCta}
                    ctaButtonText={ctaButtonText}
                    ctaUrl={ctaUrl}
                    productKey={productKey}
                />
            </div>
        </div>
    );
};

CompoundSubNav.displayName = "CompoundSubNav";

export const GlobalCompoundNav = ({
    productKey = "home",
    isShowSearchBarAndProfileContainer = true,
    isShowTryNowLink = false,
    isShowSearchButton = true,
    globalLinksProps,
    compoundSubNavLinksProps,
    compoundSubNavTitle,
    isShowCta,
    ctaButtonText,
    ctaUrl
}: GlobalCompondNavProps) => {
    const itemListArray = useMemo(
        () => [...globalLinksProps, ...compoundSubNavLinksProps],
        [globalLinksProps, compoundSubNavLinksProps]
    );

    const itemList = useMemo(() => {
        return itemListArray.map((link) => link.globalLinksKey);
    }, [itemListArray]);

    return (
        <div
            className="global-compond-nav-container"
            css={globalCompoundNavCss}
        >
            <div className="global-compond-nav">
                <GlobalNavProvider itemList={itemList}>
                    <GlobalNavNavigationBar
                        productKey={productKey}
                        isShowSearchBarAndProfileContainer={
                            isShowSearchBarAndProfileContainer
                        }
                        isShowTryNowLink={isShowTryNowLink}
                        isShowSearchButton={isShowSearchButton}
                        globalLinksProps={globalLinksProps}
                    />
                    <CompoundSubNav
                        compoundSubNavTitle={compoundSubNavTitle}
                        compoundSubNavLinksProps={compoundSubNavLinksProps}
                        isShowCta={isShowCta}
                        ctaButtonText={ctaButtonText}
                        ctaUrl={ctaUrl}
                        productKey={productKey}
                    />
                </GlobalNavProvider>
            </div>
            <div className="mobile-nav">Mobile Experience goes here</div>
        </div>
    );
};

GlobalCompoundNav.displayName = "GlobalCompoundNav";
